import * as React from "react";
import type { FnKey } from "@/reports/narratives";

export type SnapshotScore = { fn: FnKey; value: number };

const FN_PILL_STYLE: Record<string, string> = {
  Govern:   "bg-violet-100 text-violet-800",
  Identify: "bg-indigo-100 text-indigo-800",
  Protect:  "bg-emerald-100 text-emerald-800",
  Detect:   "bg-amber-100 text-amber-800",
  Respond:  "bg-rose-100 text-rose-800",
  Recover:  "bg-sky-100 text-sky-800",
};

function maturityLabel(score: number): string {
  if (score >= 80) return "Optimized";
  if (score >= 60) return "Defined/Managed";
  if (score >= 40) return "Developing";
  if (score > 0) return "Initial";
  return "Nonexistent";
}

function maturityColor(score: number): string {
  if (score >= 80) return "text-green-700";
  if (score >= 60) return "text-blue-700";
  if (score >= 40) return "text-amber-700";
  if (score > 0) return "text-orange-700";
  return "text-red-700";
}

function FnPill({ fn }: { fn: string }) {
  const style = FN_PILL_STYLE[fn] ?? "bg-muted text-muted-foreground";
  return (
    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full ${style}`}>
      {fn}
    </span>
  );
}

function FnPillGroup({ fns }: { fns: string[] }) {
  return (
    <div className="flex flex-wrap gap-1 justify-center leading-tight">
      {fns.map((fn) => (
        <FnPill key={fn} fn={fn} />
      ))}
    </div>
  );
}

export function AssessmentSnapshot({
  scores,
  totalControls,
}: {
  scores: SnapshotScore[];
  totalControls: number;
}) {
  const vals = scores.map((s) => s.value).filter((n) => Number.isFinite(n));
  const overall = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;

  const sorted = [...scores].sort((a, b) => b.value - a.value);
  const highestScore = sorted[0]?.value ?? 0;
  const lowestScore = sorted[sorted.length - 1]?.value ?? 0;

  // Group all functions sharing the highest/lowest score
  const strongestFns = sorted.filter((s) => s.value === highestScore).map((s) => s.fn);
  const weakestFns = sorted.filter((s) => s.value === lowestScore).map((s) => s.fn);

  // Only show distinct groups (if all tied, skip priority)
  const allTied = highestScore === lowestScore;

  const strongestLabel = strongestFns.length > 1 ? "Strongest Functions" : "Strongest Function";
  const priorityLabel = weakestFns.length > 1 ? "Priority Improvements" : "Priority Improvement";

  return (
    <div className="assessment-snapshot rounded-lg border border-border bg-muted/30 px-3 py-2 avoid-break">
      <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
        Assessment Snapshot
      </h3>
      <div className="grid grid-cols-5 gap-3">
        {/* Overall Score */}
        <div className="text-center">
          <div className="text-lg font-bold leading-tight">{overall}%</div>
          <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">Overall Score</div>
        </div>
        {/* Maturity Rating */}
        <div className="text-center">
          <div className={`text-lg font-bold leading-tight ${maturityColor(overall)}`}>
            {maturityLabel(overall)}
          </div>
          <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">Maturity Rating</div>
        </div>
        {/* Strongest Function(s) */}
        <div className="text-center">
          <div className="leading-tight"><FnPillGroup fns={strongestFns} /></div>
          <div className="text-[10px] text-muted-foreground leading-tight mt-1">{strongestLabel}</div>
        </div>
        {/* Priority Improvement(s) */}
        <div className="text-center">
          {allTied ? (
            <div className="text-xs text-muted-foreground leading-tight mt-1">All functions tied</div>
          ) : (
            <>
              <div className="leading-tight"><FnPillGroup fns={weakestFns} /></div>
              <div className="text-[10px] text-muted-foreground leading-tight mt-1">{priorityLabel}</div>
            </>
          )}
        </div>
        {/* Controls Evaluated */}
        <div className="text-center">
          <div className="text-lg font-bold leading-tight">{totalControls}</div>
          <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">Controls Evaluated</div>
        </div>
      </div>
    </div>
  );
}
